import React, { useMemo, useState } from "react";
import moment from "moment";
import CopyToClipboard from "./CopyToClipboard";
import ReactPaginate from "react-paginate";
import { useRequiredAuth } from "../guard/requiredAuth";
import BackdropLoader from "./BackdropLoader";
import TableSkeleton from "./TableSkeleton";
import { IconButton, TextField, MenuItem, Checkbox } from "@mui/material";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import StatusLabel from "./StatusLabel";

// Helper: Safe nested access
const getValueByPath = (obj, path) => {
  if (!obj || !path) return "";
  return path
    .split(".")
    .reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

export const formatValue = (value, format = "text", options = {}) => {
  if (value == null || value === "") return options?.defaultValue || "-";

  switch (format) {
    case "date":
      return moment(value).format("ll");

    case "number":
      return Number(value).toLocaleString();

    case "money": {
      const {
        showCurrencySymbol = false,
        currency = "USD",
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
      } = options;
      if (showCurrencySymbol) {
        return Number(value).toLocaleString("en-US", {
          style: "currency",
          currency,
          minimumFractionDigits,
        });
      } else {
        return Number(value).toLocaleString("en-US", {
          minimumFractionDigits,
          maximumFractionDigits,
        });
      }
    }

    case "status": {
      const {
        color = "secondary",
        tooltip = "",
        size = "sm",
        icon = null,
        variant = "solid",
      } = options;
      return (
        <StatusLabel
          label={String(value)}
          color={
            typeof options?.color === "function" ? options?.color(value) : color
          }
          tooltip={
            typeof options?.tooltip === "function"
              ? options?.tooltip(value)
              : tooltip
          }
          icon={
            typeof options?.icon === "function" ? options?.icon(value) : icon
          }
          variant={variant}
          size={size}
        />
      );
    }

    default:
      return String(value);
  }
};

const AdvancedDataTable = ({
  isLoading,
  isBackdropLoading,
  removeHover = false,
  columns = [],
  data = [],
  onRowClick,
  actionsRenderer,
  pageCount,
  pageSize,
  onPageSizeChange,
  onPageChange,
  filters = {},
  showSearch = false,
  onDeepSearch,
  enableRowSelection,
  selectedRows = [],
  onSelectRow,
  allSelected = false,
  onSelectAll,
  enableExpandRow,
  renderExpandedRow,
}) => {
  const { hasPermissions } = useRequiredAuth();
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortState, setSortState] = useState({
    accessor: null,
    direction: null,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [localFilters, setLocalFilters] = useState({});
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const handleSort = (accessor) => {
    if (!accessor) return;
    setSortState((prev) => {
      const nextDir =
        prev.accessor === accessor
          ? prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
            ? null
            : "asc"
          : "asc";
      return { accessor: nextDir ? accessor : null, direction: nextDir };
    });
  };

  const filteredData = useMemo(() => {
    let filtered = [...data];

    Object.entries(localFilters).forEach(([field, value]) => {
      if (value) {
        filtered = filtered.filter((row) => {
          const rowValue = getValueByPath(row, field);
          return rowValue
            ?.toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase());
        });
      }
    });
    if (searchTerm) {
      filtered = filtered.filter((row) =>
        JSON.stringify(row).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [data, localFilters, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortState.accessor || !sortState.direction) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = getValueByPath(a, sortState.accessor);
      const bVal = getValueByPath(b, sortState.accessor);
      if (aVal == null || bVal == null) return 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortState.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortState.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortState]);

  const totalCols =
    (columns || []).length +
    (!!enableRowSelection || !!enableExpandRow ? 1 : 0) +
    (!!actionsRenderer ? 1 : 0);

  return (
    <div className="position-relative">
      {isBackdropLoading && <BackdropLoader isLoading />}

      {/* Filters + Search */}
      {(Object.keys(filters).length > 0 || showSearch) && (
        <div className="d-flex justify-content-end align-items-center mb-2">
          {Object.entries(filters).map(([key, config]) => (
            <TextField
              key={key}
              size="small"
              className="mr-2"
              select={config.type === "select"}
              type={config.type === "date" ? "date" : "text"}
              label={config.label || key}
              value={localFilters[key] || ""}
              onChange={(e) =>
                setLocalFilters({ ...localFilters, [key]: e.target.value })
              }
              style={{ width: 200 }}
              disabled={
                isLoading || isBackdropLoading || !data || config.loading
              }
            >
              {config.type === "select" &&
                config.options.map((opt) => (
                  <MenuItem key={opt.value || opt} value={opt.value || opt}>
                    {opt.label || opt.name || opt}
                  </MenuItem>
                ))}
            </TextField>
          ))}

          {showSearch && (
            <TextField
              size="small"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
              className="mr-2"
              disabled={isLoading || isBackdropLoading || !data}
            />
          )}

          {onDeepSearch && (
            <button
              className="btn btn-info btn-sm"
              disabled={!searchTerm}
              onClick={() => onDeepSearch(searchTerm)}
            >
              <SearchRoundedIcon />
              GO
            </button>
          )}
        </div>
      )}

      <table className="table table-sm table-hover table-striped fixTableHead mt-2">
        <thead>
          <tr style={{ backgroundColor: "#f0f2f5" }}>
            {!!enableRowSelection || !!enableExpandRow ? (
              <th
                className="text-right"
                style={{
                  width:
                    !!enableRowSelection && !!enableExpandRow
                      ? "60px"
                      : enableRowSelection || enableExpandRow
                      ? "30px"
                      : "",
                  borderRight: "1px solid #ccc",
                }}
              >
                {!!onSelectAll && (
                  <Checkbox
                    size="small"
                    checked={!!isAllSelected}
                    onChange={(e) => {
                      onSelectAll?.(!isAllSelected);
                      setIsAllSelected(!isAllSelected);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!!allSelected || isLoading || isBackdropLoading}
                  />
                )}
              </th>
            ) : null}
            {columns.map((col, i) => {
              const isSorted =
                !!col.sortable && sortState?.accessor === col.accessor;
              return (
                <th
                  key={i}
                  className={`text-${col.align || "left"}`}
                  style={{
                    borderRight: "1px solid #ccc",
                  }}
                >
                  {col.label}
                  {col.sortable && (
                    <IconButton
                      aria-label="sort"
                      size="small"
                      className="ml-1"
                      onClick={() => col.sortable && handleSort(col.accessor)}
                      color={!!isSorted ? "primary" : "default"}
                    >
                      {sortState.direction === "asc" && !!isSorted ? (
                        <ArrowUpwardRoundedIcon fontSize="inherit" />
                      ) : sortState.direction === "desc" && !!isSorted ? (
                        <ArrowDownwardRoundedIcon fontSize="inherit" />
                      ) : (
                        <SwapVertRoundedIcon fontSize="inherit" />
                      )}
                    </IconButton>
                  )}
                </th>
              );
            })}
            {actionsRenderer && <th className="text-right">Actions</th>}
          </tr>
        </thead>

        {isLoading ? (
          <TableSkeleton cols={totalCols} />
        ) : (
          <tbody>
            {sortedData.map((row, index) => {
              const isExpanded = expandedRow?.id === row.id;
              const isHovered = hoveredRow === row || isExpanded;
              return (
                <React.Fragment key={index}>
                  <tr
                    onMouseEnter={() => !removeHover && setHoveredRow(row)}
                    onFocus={() => !removeHover && setHoveredRow(row)}
                    onBlur={() => !removeHover && setHoveredRow(null)}
                    onMouseLeave={() => !removeHover && setHoveredRow(null)}
                    style={{
                      backgroundColor: isExpanded
                        ? "#b3d9ff !important"
                        : isHovered
                        ? `#cdeef8 !important`
                        : "",
                      border: isExpanded
                        ? "2px solid #0080ff"
                        : isHovered
                        ? "2px solid #376fc8"
                        : "none",
                      cursor: isHovered && !!onRowClick ? "pointer" : "",
                      boxShadow: isHovered
                        ? "inset 0px 0px 0px 1px transparent"
                        : "",
                      transition: "background-color 0.2s ease-in-out",
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {(enableRowSelection || enableExpandRow) && (
                      <td>
                        <div className="d-flex align-items-center">
                          {enableExpandRow && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRow(
                                  isExpanded ? null : { id: row.id }
                                );
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLessOutlinedIcon fontSize="inherit" />
                              ) : (
                                <ExpandMoreOutlinedIcon fontSize="inherit" />
                              )}
                            </IconButton>
                          )}
                          {enableRowSelection && (
                            <Checkbox
                              size="small"
                              checked={selectedRows.includes(row.id)}
                              onChange={(e) =>
                                onSelectRow?.(row, e.target.checked)
                              }
                              onClick={(e) => e.stopPropagation()}
                              disabled={
                                isExpanded || isLoading || isBackdropLoading
                              }
                            />
                          )}
                        </div>
                      </td>
                    )}
                    {columns.map((col, ci) => {
                      const rawValue = col.accessor
                        ? getValueByPath(row, col.accessor)
                        : undefined;
                      const value = rawValue ?? col.defaultValue ?? "-";
                      const formatted = col.render
                        ? col.render(row)
                        : formatValue(value, col.format, col.formatOptions);

                      return (
                        <td
                          key={ci}
                          className={`text-${
                            col.align || "left"
                          } text-truncate`}
                        >
                          {col.isCopyable ? (
                            <CopyToClipboard value={formatted}>
                              {formatted}
                            </CopyToClipboard>
                          ) : (
                            formatted
                          )}
                        </td>
                      );
                    })}
                    {actionsRenderer && (
                      <td className="text-right">
                        {actionsRenderer(row)
                          .filter(
                            (action) =>
                              !action.permissions ||
                              hasPermissions(action.permissions)
                          )
                          .map((action, i) => {
                            const ThisIcon = action.icon;
                            return action.type === "icon" ? (
                              <IconButton
                                aria-label={
                                  action.label ||
                                  action.tooltip ||
                                  "icon button"
                                }
                                size={action.size || "small"}
                                color={action.color}
                                className={`mr-2`}
                                disabled={action.isDisabled || action.isLoading}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick?.(row);
                                }}
                                title={action.tooltip || action.label}
                              >
                                {!!ThisIcon && <ThisIcon fontSize="inherit" />}
                              </IconButton>
                            ) : (
                              <button
                                key={i}
                                className={`btn btn-${
                                  action.color || "primary"
                                } btn-sm m-1 ${action.className || ""}`}
                                disabled={action.isDisabled || action.isLoading}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick?.(row);
                                }}
                              >
                                {!!ThisIcon && <ThisIcon />}
                                {action.label}
                              </button>
                            );
                          })}
                      </td>
                    )}
                  </tr>

                  {isExpanded && renderExpandedRow && (
                    <tr>
                      <td colSpan={totalCols}>{renderExpandedRow(row)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {sortedData.length === 0 && (
              <tr>
                <td colSpan={totalCols} className="text-center text-muted py-3">
                  {data.length === 0
                    ? "No data available."
                    : "No results found for your filter or search."}
                </td>
              </tr>
            )}
          </tbody>
        )}
      </table>

      {!isLoading && pageCount > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3">
          <ReactPaginate
            breakLabel="..."
            nextLabel={
              <span>
                <span className="pr-1 d-none d-md-inline">Next</span>
                <i className="fas fa-angle-double-right"></i>
              </span>
            }
            previousLabel={
              <span>
                <i className="fas fa-angle-double-left"></i>
                <span className="pl-1 d-none d-md-inline">Previous</span>
              </span>
            }
            pageRangeDisplayed={3}
            onPageChange={onPageChange}
            pageCount={pageCount}
            renderOnZeroPageCount={null}
            containerClassName="pagination"
            pageLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1"
            previousLinkClassName="btn btn-outline-info btn-sm mr-1 ml-1"
            nextLinkClassName="btn btn-outline-info btn-sm ml-1"
            activeLinkClassName="active"
          />

          <select
            className="custom-select custom-select-sm ml-2"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={(e) => {
              const newSize = +e.target.value;
              window.localStorage.setItem("ITEM_PER_PAGE", newSize);
              onPageSizeChange?.(newSize);
            }}
          >
            {[10, 50, 100].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default AdvancedDataTable;
